type Kind = "welcome" | "governance" | "finance" | "resident" | "security" | "hr" | "legal" | "complete";

export function TourIllustration({ kind }: { kind: Kind }) {
  return (
    <div className="w-full max-w-sm aspect-square">
      <svg viewBox="0 0 200 200" className="w-full h-full">
        <style>{`
          @keyframes pulse-soft { 0%,100% { opacity: .6; } 50% { opacity: 1; } }
          @keyframes drift { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
          @keyframes draw { from { stroke-dashoffset: 200; } to { stroke-dashoffset: 0; } }
          @keyframes spin-slow { from { transform: rotate(0); } to { transform: rotate(360deg); } }
          @keyframes fill-bar { from { width: 0; } to { width: var(--w, 70%); } }
          @keyframes pop-in { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
          @keyframes flow { 0% { stroke-dashoffset: 30; } 100% { stroke-dashoffset: 0; } }
          .pulse { animation: pulse-soft 2s ease-in-out infinite; }
          .drift { animation: drift 3s ease-in-out infinite; }
          .drift-2 { animation: drift 3s ease-in-out infinite .5s; }
          .drift-3 { animation: drift 3s ease-in-out infinite 1s; }
          .draw { stroke-dasharray: 200; animation: draw 2s ease-out forwards; }
          .spin { transform-origin: center; animation: spin-slow 12s linear infinite; }
          .pop { animation: pop-in .6s ease-out forwards; transform-origin: center; }
          .pop-1 { animation: pop-in .6s ease-out .1s forwards; transform-origin: center; opacity: 0; }
          .pop-2 { animation: pop-in .6s ease-out .3s forwards; transform-origin: center; opacity: 0; }
          .pop-3 { animation: pop-in .6s ease-out .5s forwards; transform-origin: center; opacity: 0; }
          .pop-4 { animation: pop-in .6s ease-out .7s forwards; transform-origin: center; opacity: 0; }
          .flow { stroke-dasharray: 6 4; animation: flow 1s linear infinite; }
        `}</style>

        {kind === "welcome" && (
          <g>
            {/* Building */}
            <rect x="50" y="60" width="100" height="120" rx="4" fill="#0c4a6e" />
            <rect x="60" y="70" width="80" height="100" fill="#0ea5e9" opacity="0.2" />
            {[0, 1, 2, 3, 4].map((row) =>
              [0, 1, 2].map((col) => (
                <rect
                  key={`${row}-${col}`}
                  x={68 + col * 24}
                  y={78 + row * 18}
                  width={14}
                  height={10}
                  fill="#fde68a"
                  className="pulse"
                  style={{ animationDelay: `${(row + col) * 0.2}s` }}
                />
              ))
            )}
            {/* Door */}
            <rect x="92" y="160" width="16" height="20" fill="#92400e" />
            {/* Sun */}
            <circle cx="160" cy="40" r="14" fill="#fbbf24" className="pulse" />
            <g stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" className="spin">
              <line x1="160" y1="18" x2="160" y2="10" />
              <line x1="160" y1="70" x2="160" y2="62" />
              <line x1="138" y1="40" x2="130" y2="40" />
              <line x1="190" y1="40" x2="182" y2="40" />
            </g>
            {/* Welcome banner */}
            <rect x="40" y="20" width="100" height="22" rx="3" fill="#f59e0b" className="pop" />
            <text x="90" y="35" textAnchor="middle" fontSize="11" fontWeight="bold" fill="white">¡BIENVENIDO!</text>
          </g>
        )}

        {kind === "governance" && (
          <g>
            {/* Table */}
            <rect x="40" y="110" width="120" height="6" fill="#475569" />
            <rect x="50" y="116" width="6" height="50" fill="#475569" />
            <rect x="144" y="116" width="6" height="50" fill="#475569" />
            {/* Board members around the table */}
            <circle cx="60" cy="90" r="12" fill="#0ea5e9" className="pop-1" />
            <circle cx="100" cy="80" r="12" fill="#f59e0b" className="pop-2" />
            <circle cx="140" cy="90" r="12" fill="#10b981" className="pop-3" />
            <circle cx="100" cy="140" r="12" fill="#a855f7" className="pop-4" />
            {/* Document floating up */}
            <g className="drift">
              <rect x="80" y="30" width="40" height="50" rx="2" fill="white" stroke="#0c4a6e" strokeWidth="1.5" />
              <line x1="86" y1="42" x2="114" y2="42" stroke="#94a3b8" strokeWidth="1.5" />
              <line x1="86" y1="50" x2="114" y2="50" stroke="#94a3b8" strokeWidth="1.5" />
              <line x1="86" y1="58" x2="108" y2="58" stroke="#94a3b8" strokeWidth="1.5" />
              <circle cx="105" cy="70" r="6" fill="#10b981" />
              <path d="M102 70 l2 2 l4 -4" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
          </g>
        )}

        {kind === "finance" && (
          <g>
            {/* Chart background */}
            <rect x="20" y="20" width="160" height="160" rx="6" fill="white" stroke="#e2e8f0" />
            {/* Y axis */}
            <line x1="40" y1="40" x2="40" y2="160" stroke="#cbd5e1" />
            <line x1="40" y1="160" x2="170" y2="160" stroke="#cbd5e1" />
            {/* Bars */}
            <rect x="55" y="120" width="14" height="40" fill="#0ea5e9" className="pop-1" />
            <rect x="80" y="100" width="14" height="60" fill="#0ea5e9" className="pop-2" />
            <rect x="105" y="80" width="14" height="80" fill="#0ea5e9" className="pop-3" />
            <rect x="130" y="60" width="14" height="100" fill="#10b981" className="pop-4" />
            {/* Dollar sign */}
            <g className="drift">
              <circle cx="100" cy="40" r="15" fill="#10b981" />
              <text x="100" y="46" textAnchor="middle" fontSize="18" fontWeight="bold" fill="white">$</text>
            </g>
            {/* Trend line */}
            <path d="M55 140 L 87 120 L 112 100 L 137 80" stroke="#f59e0b" strokeWidth="2" fill="none" className="draw" />
          </g>
        )}

        {kind === "resident" && (
          <g>
            {/* Phone */}
            <rect x="60" y="20" width="80" height="160" rx="10" fill="#0c4a6e" />
            <rect x="65" y="30" width="70" height="140" rx="3" fill="#f1f5f9" />
            {/* App icon */}
            <rect x="78" y="40" width="44" height="44" rx="8" fill="#0ea5e9" />
            <path d="M88 60 l8 8 l16 -16" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" className="draw" />
            {/* Status list */}
            <rect x="75" y="95" width="50" height="6" rx="1" fill="#94a3b8" className="pop-1" />
            <rect x="75" y="106" width="40" height="6" rx="1" fill="#94a3b8" className="pop-2" />
            <rect x="75" y="117" width="45" height="6" rx="1" fill="#94a3b8" className="pop-3" />
            {/* Submit button */}
            <rect x="75" y="135" width="50" height="20" rx="3" fill="#10b981" className="pulse" />
            <text x="100" y="148" textAnchor="middle" fontSize="9" fontWeight="bold" fill="white">ENVIAR</text>
            {/* Notification dot */}
            <circle cx="130" cy="30" r="6" fill="#ef4444" className="pulse" />
            <text x="130" y="33" textAnchor="middle" fontSize="8" fontWeight="bold" fill="white">3</text>
          </g>
        )}

        {kind === "security" && (
          <g>
            {/* Shield */}
            <g className="drift">
              <path d="M100 25 L140 40 L140 100 Q140 140 100 165 Q60 140 60 100 L60 40 Z" fill="#dc2626" />
              <path d="M100 35 L130 47 L130 100 Q130 132 100 152 Q70 132 70 100 L70 47 Z" fill="#fee2e2" />
              <path d="M82 95 l12 14 l24 -28" stroke="#dc2626" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            {/* Camera lens */}
            <g className="spin">
              <circle cx="40" cy="55" r="12" fill="#1e293b" />
              <circle cx="40" cy="55" r="8" fill="#475569" />
              <circle cx="40" cy="55" r="4" fill="#0ea5e9" />
            </g>
            {/* Key */}
            <g className="drift-2">
              <circle cx="155" cy="155" r="10" fill="none" stroke="#f59e0b" strokeWidth="3" />
              <line x1="160" y1="160" x2="175" y2="175" stroke="#f59e0b" strokeWidth="3" />
              <line x1="170" y1="170" x2="175" y2="165" stroke="#f59e0b" strokeWidth="3" />
            </g>
          </g>
        )}

        {kind === "hr" && (
          <g>
            {/* Kanban columns */}
            <rect x="20" y="40" width="50" height="130" rx="4" fill="#fef3c7" />
            <rect x="75" y="40" width="50" height="130" rx="4" fill="#dbeafe" />
            <rect x="130" y="40" width="50" height="130" rx="4" fill="#d1fae5" />
            <text x="45" y="55" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#92400e">PEND</text>
            <text x="100" y="55" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#1e40af">PROC</text>
            <text x="155" y="55" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#065f46">DONE</text>
            {/* Cards */}
            <rect x="25" y="65" width="40" height="22" rx="2" fill="white" className="pop-1" />
            <rect x="25" y="92" width="40" height="22" rx="2" fill="white" className="pop-2" />
            <rect x="80" y="65" width="40" height="22" rx="2" fill="white" className="pop-3" />
            <rect x="135" y="65" width="40" height="22" rx="2" fill="white" className="pop-4" />
            <rect x="135" y="92" width="40" height="22" rx="2" fill="white" className="pop-4" />
            {/* Clock */}
            <g className="drift">
              <circle cx="100" cy="155" r="14" fill="white" stroke="#0c4a6e" strokeWidth="2" />
              <line x1="100" y1="155" x2="100" y2="146" stroke="#0c4a6e" strokeWidth="2" strokeLinecap="round" />
              <line x1="100" y1="155" x2="107" y2="155" stroke="#0c4a6e" strokeWidth="2" strokeLinecap="round" />
            </g>
          </g>
        )}

        {kind === "legal" && (
          <g>
            {/* Folder stack */}
            <rect x="40" y="120" width="120" height="50" rx="4" fill="#92400e" />
            <rect x="50" y="85" width="120" height="50" rx="4" fill="#b45309" />
            <rect x="60" y="50" width="120" height="50" rx="4" fill="#d97706" />
            {/* Document */}
            <g className="drift">
              <rect x="80" y="30" width="60" height="80" rx="3" fill="white" stroke="#92400e" strokeWidth="1.5" />
              <text x="110" y="48" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#92400e">v2.1</text>
              <line x1="88" y1="58" x2="132" y2="58" stroke="#cbd5e1" strokeWidth="1" />
              <line x1="88" y1="66" x2="132" y2="66" stroke="#cbd5e1" strokeWidth="1" />
              <line x1="88" y1="74" x2="132" y2="74" stroke="#cbd5e1" strokeWidth="1" />
              <line x1="88" y1="82" x2="124" y2="82" stroke="#cbd5e1" strokeWidth="1" />
              {/* Seal */}
              <circle cx="115" cy="98" r="8" fill="#10b981" />
              <path d="M111 98 l3 3 l5 -5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            {/* Alert badges */}
            <circle cx="35" cy="40" r="10" fill="#ef4444" className="pulse" />
            <text x="35" y="44" textAnchor="middle" fontSize="9" fontWeight="bold" fill="white">!</text>
          </g>
        )}

        {kind === "complete" && (
          <g>
            {/* Success check */}
            <circle cx="100" cy="100" r="50" fill="#10b981" className="pop" />
            <path d="M75 100 l18 18 l32 -34" stroke="white" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" className="draw" />
            {/* Sparkles */}
            <g className="pulse">
              <path d="M40 60 l3 -8 l3 8 l8 3 l-8 3 l-3 8 l-3 -8 l-8 -3 z" fill="#fbbf24" />
              <path d="M160 50 l2 -5 l2 5 l5 2 l-5 2 l-2 5 l-2 -5 l-5 -2 z" fill="#fbbf24" />
              <path d="M155 155 l3 -7 l3 7 l7 3 l-7 3 l-3 7 l-3 -7 l-7 -3 z" fill="#0ea5e9" />
              <path d="M30 150 l2 -5 l2 5 l5 2 l-5 2 l-2 5 l-2 -5 l-5 -2 z" fill="#a855f7" />
            </g>
          </g>
        )}
      </svg>
    </div>
  );
}
